/**
 * ============================================================================
 * NOTIFICATION CENTER COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Real-Time Notifications - In-app notification center
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../hooks/useNotifications';
import { useNotificationStore } from '../../store';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Button from '../common/Button';
import Card from '../common/Card';
import Spinner from '../common/Spinner';
import EmptyState from '../common/EmptyState';
import NotificationItem from './NotificationItem';

/**
 * Notification Center Component
 * @param {Object} props
 * @param {Function} props.onClose - Close handler
 * @returns {JSX.Element}
 */
const NotificationCenter = ({ onClose }) => {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading } = useNotificationStore();
  const { markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const { formatDate } = useI18n();

  const [filter, setFilter] = useState('all'); // all, unread, read

  const filteredNotifications = notifications.filter((notification) => {
    const isRead = notification.isRead || notification.read;
    if (filter === 'unread') return !isRead;
    if (filter === 'read') return isRead;
    return true;
  });

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markAsRead(notificationId);
    } catch (error) {
      console.error('Mark as read error:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Mark all as read error:', error);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
    } catch (error) {
      console.error('Delete notification error:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    const isRead = notification.isRead || notification.read;
    if (!isRead) {
      handleMarkAsRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.transactionId) {
      navigate(`/transactions/${notification.transactionId}`);
    } else if (notification.disputeId) {
      navigate(`/disputes/${notification.disputeId}`);
    }

    if (onClose) onClose();
  };

  const { loadNotifications } = useNotifications();

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  return (
    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 w-full max-w-md max-h-[600px] flex flex-col overflow-hidden animate-scaleIn">
      {/* Header */}
      <div className="p-5 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent flex items-center gap-2">
            <span className="text-2xl animate-bounce">🔔</span>
            Notifications
            {unreadCount > 0 && (
              <span className="ml-2 px-2.5 py-1 bg-gradient-to-r from-blue-600 to-green-600 text-white text-xs font-bold rounded-full shadow-lg animate-pulse">
                {unreadCount}
              </span>
            )}
          </h2>
          <div className="flex gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllAsRead}
                className="text-xs"
              >
                Mark all read
              </Button>
            )}
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all transform hover:rotate-90"
                aria-label="Close notifications"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          {['all', 'unread', 'read'].map((filterType) => (
            <button
              key={filterType}
              onClick={() => setFilter(filterType)}
              className={`
                px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200
                transform hover:scale-105
                ${
                  filter === filterType
                    ? 'bg-gradient-to-r from-blue-600 to-green-600 text-white shadow-md'
                    : 'bg-white/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }
              `}
            >
              {filterType === 'all'
                ? 'All'
                : filterType === 'unread'
                ? `Unread (${unreadCount})`
                : 'Read'}
            </button>
          ))}
        </div>
      </div>

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <Spinner size="lg" />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <EmptyState
            icon="🔔"
            title={filter === 'unread' ? 'No unread notifications' : 'No notifications'}
            description={
              filter === 'unread'
                ? "You're all caught up!"
                : "You'll see notifications here when you receive them"
            }
          />
        ) : (
          filteredNotifications.map((notification, index) => (
            <NotificationItem
              key={notification.id || index}
              notification={notification}
              onClick={handleNotificationClick}
              onDelete={handleDelete}
              onMarkRead={handleMarkAsRead}
            />
          ))
        )}
      </div>

      {/* Footer */}
      {filteredNotifications.length > 0 && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <Button
            variant="outline"
            size="sm"
            fullWidth
            onClick={() => {
              navigate('/notifications');
              if (onClose) onClose();
            }}
          >
            View All Notifications →
          </Button>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
